import React, { useEffect, useRef, useState } from 'react';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';

const RosterAiTradeIdeasModal = ({
	isLoading,
	isOpen,
	onClose,
	tradeIdeas,
	step = 'type-selection', // 'type-selection', 'player-selection', 'league-player-selection', 'trade-analyzer-your-side', 'trade-analyzer-their-side', 'chat', or 'results'
	onTradeTypeSelect,
	onPlayerSelectionComplete,
	onLeaguePlayerSelectionComplete,
	onTradeAnalyzerComplete,
	userRosterPlayers = [],
	leagueRosterPlayers = [],
	selectedPlayers = [],
	conversationHistory = [],
	onSendChatMessage
}) => {
	const modalRef = useRef(null);
	const [
		localSelectedPlayers,
		setLocalSelectedPlayers
	] = useState([]);
	const [
		tradeAnalyzerYourSidePlayers,
		setTradeAnalyzerYourSidePlayers
	] = useState([]);
	const [
		chatInput,
		setChatInput
	] = useState('');
	const chatEndRef = useRef(null);

	const handleClickOutsideModal = (event) => {
		if (modalRef.current && !modalRef.current.contains(event.target)) {
			onClose();
		}
	};

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutsideModal);
		return () => {
			document.removeEventListener('mousedown', handleClickOutsideModal);
		};
	}, []);

	// Prevent background scrolling when the modal is open
	useEffect(
		() => {
			if (isOpen) {
				document.body.style.overflow = 'hidden';
			} else {
				document.body.style.overflow = 'auto';
			}
		},
		[
			isOpen
		]
	);

	// Reset local selected players when entering a selection step
	useEffect(
		() => {
			if (
				isOpen &&
				(step === 'player-selection' ||
					step === 'league-player-selection' ||
					step === 'trade-analyzer-your-side' ||
					step === 'trade-analyzer-their-side')
			) {
				setLocalSelectedPlayers([]);
			}
		},
		[
			isOpen,
			step
		]
	);

	const handlePlayerToggle = (playerName) => {
		setLocalSelectedPlayers((prev) => {
			if (prev.includes(playerName)) {
				return prev.filter((name) => name !== playerName);
			} else {
				if (prev.length >= 3) {
					return prev; // Max 3 players
				}
				return [
					...prev,
					playerName
				];
			}
		});
	};

	const handlePlayerSelectionContinue = () => {
		if (localSelectedPlayers.length === 0) {
			return; // Don't proceed if no players selected
		}

		// For trade analyzer, save the first selection and move to next step
		if (step === 'trade-analyzer-your-side') {
			setTradeAnalyzerYourSidePlayers(localSelectedPlayers);
			if (onTradeTypeSelect) {
				onTradeTypeSelect('trade-analyzer-their-side-internal');
			}
		} else if (onPlayerSelectionComplete) {
			onPlayerSelectionComplete(localSelectedPlayers);
		}
	};

	const handleLeaguePlayerSelectionContinue = () => {
		if (localSelectedPlayers.length === 0) {
			return; // Don't proceed if no players selected
		}

		// For trade analyzer, combine both selections and analyze
		if (step === 'trade-analyzer-their-side') {
			if (onTradeAnalyzerComplete) {
				onTradeAnalyzerComplete(
					tradeAnalyzerYourSidePlayers,
					localSelectedPlayers
				);
			}
		} else if (onLeaguePlayerSelectionComplete) {
			onLeaguePlayerSelectionComplete(localSelectedPlayers);
		}
	};

	const handleTradeTypeSelectClick = (tradeType) => {
		if (onTradeTypeSelect) {
			onTradeTypeSelect(tradeType);
		}
	};

	const handleSendMessage = () => {
		if (chatInput.trim().length === 0) return;

		if (onSendChatMessage) {
			onSendChatMessage(chatInput.trim());
			setChatInput('');
		}
	};

	const handleChatKeyPress = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSendMessage();
		}
	};

	// Auto-scroll chat to bottom when new messages arrive
	useEffect(
		() => {
			if (step === 'chat' && chatEndRef.current) {
				chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
			}
		},
		[
			conversationHistory,
			step
		]
	);

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
			<div
				className="relative bg-white dark:bg-dark-card border border-gray-300 dark:border-dark-border rounded-xl shadow-2xl text-gray-900 dark:text-dark-text w-full max-w-4xl max-h-[90vh] overflow-hidden mx-4"
				ref={modalRef}
			>
				{/* Header */}
				<div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border">
					<div className="flex items-center space-x-3">
						<h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text">
							AI Trade Insights
						</h2>
					</div>
					<button
						className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center transition-colors duration-200"
						onClick={onClose}
					>
						<i className="fas fa-times text-gray-600 dark:text-gray-300 text-sm" />
					</button>
				</div>

				{/* Content */}
				<div
					className="overflow-y-auto p-6"
					style={{ maxHeight: 'calc(90vh - 120px)' }}
				>
					{step === 'type-selection' ? (
						// Step 1: Trade Type Selection
						<div className="space-y-6">
							<div className="text-center mb-6">
								<p className="text-gray-600 dark:text-gray-400">
									How would you like to generate trade ideas?
								</p>
							</div>

							<div className="space-y-4">
								<button
									onClick={() => handleTradeTypeSelectClick('trade-analyzer')}
									className="w-full p-6 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-orange-500 dark:hover:border-orange-500 hover:shadow-lg transition-all duration-200 group"
								>
									<div className="flex items-start space-x-4">
										<div className="w-12 h-12 bg-gray-100 dark:bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-200">
											<i className="fas fa-balance-scale text-gray-600 dark:text-gray-400 text-xl" />
										</div>
										<div className="text-left flex-1">
											<h3 className="text-lg font-bold text-gray-900 dark:text-dark-text mb-2">
												Trade Analyzer
											</h3>
											<p className="text-gray-600 dark:text-gray-400 text-sm">
												Select players from both teams to analyze a specific
												trade proposal and get AI insights on fairness and
												impact
											</p>
										</div>
									</div>
								</button>

								<button
									onClick={() => handleTradeTypeSelectClick('targeted')}
									className="w-full p-6 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-lg transition-all duration-200 group"
								>
									<div className="flex items-start space-x-4">
										<div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-200">
											<i className="fas fa-crosshairs text-blue-600 dark:text-blue-400 text-xl" />
										</div>
										<div className="text-left flex-1">
											<h3 className="text-lg font-bold text-gray-900 dark:text-dark-text mb-2">
												Trade Away Specific Players
											</h3>
											<p className="text-gray-600 dark:text-gray-400 text-sm">
												Select up to 3 players from your roster that you want to
												trade away, and get AI trade ideas involving those
												players
											</p>
										</div>
									</div>
								</button>

								<button
									onClick={() => handleTradeTypeSelectClick('trade-for')}
									className="w-full p-6 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-gray-500 dark:hover:border-gray-500 hover:shadow-lg transition-all duration-200 group"
								>
									<div className="flex items-start space-x-4">
										<div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-200">
											<i className="fas fa-bullseye text-red-600 dark:text-red-400 text-xl" />
										</div>
										<div className="text-left flex-1">
											<h3 className="text-lg font-bold text-gray-900 dark:text-dark-text mb-2">
												Trade For Specific Players
											</h3>
											<p className="text-gray-600 dark:text-gray-400 text-sm">
												Select up to 3 players from their roster that you want
												to acquire, and get AI trade ideas to get those players
											</p>
										</div>
									</div>
								</button>

								<button
									onClick={() => handleTradeTypeSelectClick('generate')}
									className="w-full p-6 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-green-500 dark:hover:border-green-500 hover:shadow-lg transition-all duration-200 group"
								>
									<div className="flex items-start space-x-4">
										<div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-200">
											<i className="fas fa-magic text-green-600 dark:text-green-400 text-xl" />
										</div>
										<div className="text-left flex-1">
											<h3 className="text-lg font-bold text-gray-900 dark:text-dark-text mb-2">
												Surprise Me!
											</h3>
											<p className="text-gray-600 dark:text-gray-400 text-sm">
												Let AI work its magic and generate 3 creative trade
												ideas based on roster analysis and team needs
											</p>
										</div>
									</div>
								</button>

								<button
									onClick={() => handleTradeTypeSelectClick('chat')}
									className="w-full p-6 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-purple-500 dark:hover:border-purple-500 hover:shadow-lg transition-all duration-200 group"
								>
									<div className="flex items-start space-x-4">
										<div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-200">
											<i className="fas fa-comments text-purple-600 dark:text-purple-400 text-xl" />
										</div>
										<div className="text-left flex-1">
											<h3 className="text-lg font-bold text-gray-900 dark:text-dark-text mb-2">
												Ask a Question
											</h3>
											<p className="text-gray-600 dark:text-gray-400 text-sm">
												Have a conversation with our leading AI agent about
												trade strategies, player values, and specific trade
												scenarios
											</p>
										</div>
									</div>
								</button>
							</div>
						</div>
					) : step === 'player-selection' ? (
						// Step 2: Player Selection
						<div className="space-y-6">
							<div className="text-center mb-6">
								<p className="text-gray-600 dark:text-gray-400">
									Select up to 3 players from your roster to trade away
								</p>
								<p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
									{localSelectedPlayers.length} / 3 selected
								</p>
							</div>

							<div className="space-y-3">
								{userRosterPlayers.map((player, index) => {
									const playerName = `${player.first_name} ${player.last_name}`;
									const isSelected = localSelectedPlayers.includes(playerName);
									const isDisabled =
										!isSelected && localSelectedPlayers.length >= 3;

									return (
										<button
											key={index}
											onClick={() => handlePlayerToggle(playerName)}
											disabled={isDisabled}
											className={`w-full p-4 rounded-lg border-2 text-left transition-all duration-200 ${isSelected
												? 'bg-blue-50 dark:bg-blue-900/40 border-blue-500 dark:border-blue-400 shadow-sm ring-1 ring-blue-500/20'
												: isDisabled
													? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
													: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md'}`}
										>
											<div className="flex items-center justify-between">
												<div className="flex items-center space-x-3">
													<div
														className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${isSelected
															? 'bg-blue-500 border-blue-500 shadow-sm'
															: 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'}`}
													>
														{isSelected && (
															<i className="fas fa-check text-white text-xs font-bold" />
														)}
													</div>
													<div>
														<p
															className={`font-semibold ${isSelected
																? 'text-blue-700 dark:text-blue-300'
																: 'text-gray-900 dark:text-dark-text'}`}
														>
															{playerName}
														</p>
														<p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
															{player.position}
														</p>
													</div>
												</div>
											</div>
										</button>
									);
								})}
							</div>

							<div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
								<button
									onClick={onClose}
									className="px-6 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
								>
									Cancel
								</button>
								<button
									onClick={handlePlayerSelectionContinue}
									disabled={localSelectedPlayers.length === 0}
									className={`px-6 py-2.5 rounded-lg font-semibold transition-all duration-200 ${localSelectedPlayers.length ===
									0
										? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed border-2 border-gray-300 dark:border-gray-600'
										: 'bg-green-500 hover:bg-green-600 text-white shadow-sm hover:shadow-md'}`}
								>
									Continue
								</button>
							</div>
						</div>
					) : step === 'league-player-selection' ? (
						// Step 3: League Roster Player Selection (for Trade-For mode)
						<div className="space-y-6">
							<div className="text-center mb-6">
								<p className="text-gray-600 dark:text-gray-400">
									Select up to 3 players from their roster to acquire
								</p>
								<p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
									{localSelectedPlayers.length} / 3 selected
								</p>
							</div>

							<div className="space-y-3">
								{leagueRosterPlayers.map((player, index) => {
									const playerName = `${player.first_name} ${player.last_name}`;
									const isSelected = localSelectedPlayers.includes(playerName);
									const isDisabled =
										!isSelected && localSelectedPlayers.length >= 3;

									return (
										<button
											key={index}
											onClick={() => handlePlayerToggle(playerName)}
											disabled={isDisabled}
											className={`w-full p-4 rounded-lg border-2 text-left transition-all duration-200 ${isSelected
												? 'bg-blue-50 dark:bg-blue-900/40 border-blue-500 dark:border-blue-400 shadow-sm ring-1 ring-blue-500/20'
												: isDisabled
													? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
													: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md'}`}
										>
											<div className="flex items-center justify-between">
												<div className="flex items-center space-x-3">
													<div
														className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${isSelected
															? 'bg-blue-500 border-blue-500 shadow-sm'
															: 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'}`}
													>
														{isSelected && (
															<i className="fas fa-check text-white text-xs font-bold" />
														)}
													</div>
													<div>
														<p
															className={`font-semibold ${isSelected
																? 'text-blue-700 dark:text-blue-300'
																: 'text-gray-900 dark:text-dark-text'}`}
														>
															{playerName}
														</p>
														<p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
															{player.position}
														</p>
													</div>
												</div>
											</div>
										</button>
									);
								})}
							</div>

							<div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
								<button
									onClick={onClose}
									className="px-6 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
								>
									Cancel
								</button>
								<button
									onClick={handleLeaguePlayerSelectionContinue}
									disabled={localSelectedPlayers.length === 0}
									className={`px-6 py-2.5 rounded-lg font-semibold transition-all duration-200 ${localSelectedPlayers.length ===
									0
										? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed border-2 border-gray-300 dark:border-gray-600'
										: 'bg-green-500 hover:bg-green-600 text-white shadow-sm hover:shadow-md'}`}
								>
									Continue
								</button>
							</div>
						</div>
					) : step === 'trade-analyzer-your-side' ? (
						// Trade Analyzer Step 1: Select YOUR players
						<div className="space-y-6">
							<div className="text-center mb-6">
								<p className="text-gray-600 dark:text-gray-400">
									Select up to 3 players from YOUR roster that you're offering
								</p>
								<p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
									{localSelectedPlayers.length} / 3 selected
								</p>
							</div>

							<div className="space-y-3">
								{userRosterPlayers.map((player, index) => {
									const playerName = `${player.first_name} ${player.last_name}`;
									const isSelected = localSelectedPlayers.includes(playerName);
									const isDisabled =
										!isSelected && localSelectedPlayers.length >= 3;

									return (
										<button
											key={index}
											onClick={() => handlePlayerToggle(playerName)}
											disabled={isDisabled}
											className={`w-full p-4 rounded-lg border-2 text-left transition-all duration-200 ${isSelected
												? 'bg-blue-50 dark:bg-blue-900/40 border-blue-500 dark:border-blue-400 shadow-sm ring-1 ring-blue-500/20'
												: isDisabled
													? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
													: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md'}`}
										>
											<div className="flex items-center justify-between">
												<div className="flex items-center space-x-3">
													<div
														className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${isSelected
															? 'bg-blue-500 border-blue-500 shadow-sm'
															: 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'}`}
													>
														{isSelected && (
															<i className="fas fa-check text-white text-xs font-bold" />
														)}
													</div>
													<div>
														<p
															className={`font-semibold ${isSelected
																? 'text-blue-700 dark:text-blue-300'
																: 'text-gray-900 dark:text-dark-text'}`}
														>
															{playerName}
														</p>
														<p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
															{player.position}
														</p>
													</div>
												</div>
											</div>
										</button>
									);
								})}
							</div>

							<div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
								<button
									onClick={onClose}
									className="px-6 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
								>
									Cancel
								</button>
								<button
									onClick={handlePlayerSelectionContinue}
									disabled={localSelectedPlayers.length === 0}
									className={`px-6 py-2.5 rounded-lg font-semibold transition-all duration-200 ${localSelectedPlayers.length ===
									0
										? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed border-2 border-gray-300 dark:border-gray-600'
										: 'bg-green-500 hover:bg-green-600 text-white shadow-sm hover:shadow-md'}`}
								>
									Next: Select Their Players
								</button>
							</div>
						</div>
					) : step === 'trade-analyzer-their-side' ? (
						// Trade Analyzer Step 2: Select THEIR players
						<div className="space-y-6">
							<div className="text-center mb-6">
								<p className="text-gray-600 dark:text-gray-400">
									Select up to 3 players from THEIR roster that you'd receive
								</p>
								<p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
									{localSelectedPlayers.length} / 3 selected
								</p>
							</div>

							<div className="space-y-3">
								{leagueRosterPlayers.map((player, index) => {
									const playerName = `${player.first_name} ${player.last_name}`;
									const isSelected = localSelectedPlayers.includes(playerName);
									const isDisabled =
										!isSelected && localSelectedPlayers.length >= 3;

									return (
										<button
											key={index}
											onClick={() => handlePlayerToggle(playerName)}
											disabled={isDisabled}
											className={`w-full p-4 rounded-lg border-2 text-left transition-all duration-200 ${isSelected
												? 'bg-blue-50 dark:bg-blue-900/40 border-blue-500 dark:border-blue-400 shadow-sm ring-1 ring-blue-500/20'
												: isDisabled
													? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
													: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md'}`}
										>
											<div className="flex items-center justify-between">
												<div className="flex items-center space-x-3">
													<div
														className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${isSelected
															? 'bg-blue-500 border-blue-500 shadow-sm'
															: 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'}`}
													>
														{isSelected && (
															<i className="fas fa-check text-white text-xs font-bold" />
														)}
													</div>
													<div>
														<p
															className={`font-semibold ${isSelected
																? 'text-blue-700 dark:text-blue-300'
																: 'text-gray-900 dark:text-dark-text'}`}
														>
															{playerName}
														</p>
														<p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
															{player.position}
														</p>
													</div>
												</div>
											</div>
										</button>
									);
								})}
							</div>

							<div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
								<button
									onClick={onClose}
									className="px-6 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
								>
									Cancel
								</button>
								<button
									onClick={handleLeaguePlayerSelectionContinue}
									disabled={localSelectedPlayers.length === 0}
									className={`px-6 py-2.5 rounded-lg font-semibold transition-all duration-200 ${localSelectedPlayers.length ===
									0
										? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed border-2 border-gray-300 dark:border-gray-600'
										: 'bg-green-500 hover:bg-green-600 text-white shadow-sm hover:shadow-md'}`}
								>
									Analyze Trade
								</button>
							</div>
						</div>
					) : step === 'chat' ? (
						// Step 3: Chat Interface
						<div
							className="flex flex-col h-full"
							style={{ height: 'calc(90vh - 180px)' }}
						>
							{/* Messages Container */}
							<div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
								{conversationHistory.length === 0 ? (
									<div className="text-center py-12">
										<div className="w-16 h-16 bg-purple-100 dark:bg-purple-900 rounded-xl flex items-center justify-center mx-auto mb-4">
											<i className="fas fa-comments text-purple-500 text-2xl" />
										</div>
										<h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-2">
											Start a Conversation
										</h3>
										<p className="text-gray-600 dark:text-gray-400 text-sm max-w-md mx-auto">
											Ask our AI agent any questions about these rosters. Our AI
											agent has full context of both teams and can help you
											evaluate specific scenarios.
										</p>
									</div>
								) : (
									<React.Fragment>
										{conversationHistory.map((message, index) => (
											<div
												key={index}
												className={`flex ${message.role === 'user'
													? 'justify-end'
													: 'justify-start'}`}
											>
												<div
													className={`max-w-[80%] rounded-lg p-4 ${message.role ===
													'user'
														? 'bg-blue-500 text-white'
														: 'bg-white dark:bg-gray-800 text-gray-900 dark:text-dark-text border border-gray-200 dark:border-gray-700'}`}
												>
													<div className="flex items-start space-x-2">
														<div className="flex-shrink-0">
															{message.role === 'user' ? (
																<i className="fas fa-user text-sm" />
															) : (
																<i className="fas fa-robot text-sm text-purple-500" />
															)}
														</div>
														<div className="flex-1 text-sm leading-relaxed whitespace-pre-wrap">
															{message.content}
														</div>
													</div>
												</div>
											</div>
										))}
										{isLoading && (
											<div className="flex justify-start">
												<div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
													<div className="flex items-center space-x-2">
														<i className="fas fa-robot text-sm text-purple-500" />
														<div className="flex space-x-1">
															<div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" />
															<div
																className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
																style={{ animationDelay: '0.1s' }}
															/>
															<div
																className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
																style={{ animationDelay: '0.2s' }}
															/>
														</div>
													</div>
												</div>
											</div>
										)}
										<div ref={chatEndRef} />
									</React.Fragment>
								)}
							</div>

							{/* Input Container */}
							<div className="mt-4 flex space-x-2">
								<textarea
									value={chatInput}
									onChange={(e) => setChatInput(e.target.value)}
									onKeyPress={handleChatKeyPress}
									placeholder="Ask about trade strategies, player values, or specific scenarios..."
									disabled={isLoading}
									className="flex-1 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-dark-text resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
									rows={3}
								/>
								<button
									onClick={handleSendMessage}
									disabled={isLoading || chatInput.trim().length === 0}
									className={`px-6 py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center space-x-2 ${isLoading ||
									chatInput.trim().length === 0
										? 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
										: 'bg-purple-500 hover:bg-purple-600 text-white'}`}
								>
									<i className="fas fa-paper-plane" />
									<span>Send</span>
								</button>
							</div>
						</div>
					) : (
						// Step 4: Results
						<React.Fragment>
							{isLoading ? (
								<div className="flex items-center justify-center py-12">
									<LoadingSpinner />
								</div>
							) : tradeIdeas && tradeIdeas.length > 0 ? (
								<div className="space-y-6">
									{tradeIdeas.map((trade, index) => (
										<div
											key={index}
											className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow duration-200"
										>
											{/* Trade Header */}
											<div className="flex items-center mb-6">
												<div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center mr-3">
													<span className="text-blue-600 dark:text-blue-400 font-semibold text-sm">
														{index + 1}
													</span>
												</div>
												<h3 className="text-lg font-bold text-gray-900 dark:text-dark-text">
													Trade Idea {index + 1}
												</h3>
											</div>

											{/* Trade Details */}
											<div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
												<div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
													<div className="flex items-center mb-2">
														<div className="w-2 h-2 bg-red-500 rounded-full mr-2" />
														<h4 className="font-semibold text-red-800 dark:text-red-400">
															You Give Up
														</h4>
													</div>
													<p className="text-red-700 dark:text-red-300 text-sm leading-relaxed whitespace-pre-line">
														{Array.isArray(trade.your_side) ? (
															trade.your_side.join(', ')
														) : (
															trade.your_side || 'N/A'
														)}
													</p>
												</div>
												<div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
													<div className="flex items-center mb-2">
														<div className="w-2 h-2 bg-green-500 rounded-full mr-2" />
														<h4 className="font-semibold text-green-800 dark:text-green-400">
															You Receive
														</h4>
													</div>
													<p className="text-green-700 dark:text-green-300 text-sm leading-relaxed whitespace-pre-line">
														{Array.isArray(trade.their_side) ? (
															trade.their_side.join(', ')
														) : (
															trade.their_side || 'N/A'
														)}
													</p>
												</div>
											</div>

											{/* Analysis */}
											<div className="space-y-4">
												<div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
													<h4 className="font-semibold text-gray-900 dark:text-dark-text mb-2 flex items-center">
														<i className="fas fa-lightbulb text-blue-500 mr-2" />
														Why This Trade Works
													</h4>
													<p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
														{trade.rationale || 'N/A'}
													</p>
												</div>

												<div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
													<h4 className="font-semibold text-gray-900 dark:text-dark-text mb-2 flex items-center">
														<i className="fas fa-bolt text-purple-500 mr-2" />
														Roster Impact
													</h4>
													<p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
														{trade.roster_impact || 'N/A'}
													</p>
												</div>
											</div>
										</div>
									))}
								</div>
							) : (
								<div className="text-center py-12">
									<div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center mx-auto mb-4">
										<i className="fas fa-exchange-alt text-gray-400 text-2xl" />
									</div>
									<p className="text-gray-500 dark:text-gray-400 text-lg font-medium">
										No trade ideas available
									</p>
									<p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
										Try again or check back later
									</p>
								</div>
							)}
						</React.Fragment>
					)}
				</div>
			</div>
		</div>
	);
};

export default RosterAiTradeIdeasModal;
